/**
 * GLP-1 contraindication screening constants and helpers.
 * Single source of truth — used by GetStarted, ProviderDashboard, and PatientManagement.
 */

export const GLP1_DISQUALIFYING_CONDITIONS = [
  "Diabetes",
  "Eating disorder (current or history)",
  "Gallbladder disease",
  "Medullary thyroid carcinoma (personal or family history)",
  "Multiple endocrine neoplasia type 2 (MEN2)",
  "Pancreatitis (personal history)",
  "Diabetic retinopathy",
  "Currently pregnant",
] as const;

/** All conditions shown in the medical screening step (includes non-disqualifying ones). */
export const MEDICAL_SCREENING_CONDITIONS = [
  ...GLP1_DISQUALIFYING_CONDITIONS,
  "Heart disease",
  "Kidney disease",
  "High blood pressure",
  "None of the above",
] as const;

/** Check whether a set of selected conditions includes any GLP-1 disqualifier. */
export const hasGlp1ConditionDisqualifier = (conditions: string[]): boolean =>
  conditions.some((c) => (GLP1_DISQUALIFYING_CONDITIONS as readonly string[]).includes(c));

/**
 * Parse an assessment's stored data and return a list of GLP-1 disqualifying flags.
 * Works with the comma-separated `conditions` column and the free-text `additional_info` column.
 */
export const getGlp1Flags = (
  conditions: string | null,
  additionalInfo: string | null
): string[] => {
  const flags: string[] = [];

  if (conditions) {
    conditions
      .split(",")
      .map((c) => c.trim())
      .forEach((c) => {
        if ((GLP1_DISQUALIFYING_CONDITIONS as readonly string[]).includes(c)) {
          flags.push(c);
        }
      });
  }

  const info = additionalInfo || "";
  if (/bariatric surgery.*yes/i.test(info)) flags.push("Recent bariatric surgery");
  if (/pregnant.*breastfeeding.*yes/i.test(info) || /pregnant.*TTC.*yes/i.test(info))
    flags.push("Pregnant/breastfeeding/TTC");
  if (/substance misuse.*yes/i.test(info)) flags.push("Substance misuse history");
  if (/BMI.*under 25/i.test(info) || /low BMI/i.test(info)) flags.push("BMI under 25");

  return flags;
};
